// Content detail shown in a modal: asset metadata, latest version's SEO fields,
// and publish-target statuses. The list/detail endpoints do not currently expose
// the full article body, so we surface the SEO metadata that IS returned and note
// the body isn't available via the API (see delivery report assumptions).

import {
  Badge,
  BlockStack,
  Box,
  Divider,
  InlineStack,
  Modal,
  Text,
} from "@shopify/polaris";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../api/hooks";
import { LoadingCard, ErrorState } from "../components/StateViews";
import { StatusBadge } from "../components/StatusBadge";
import { formatDateTime, titleCase } from "../lib/format";
import { sanitizeHtml } from "../lib/sanitize";

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <BlockStack gap="050">
      <Text as="span" tone="subdued" variant="bodySm">
        {label}
      </Text>
      <Text as="span">{value && value.length ? value : "—"}</Text>
    </BlockStack>
  );
}

export function ContentDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const api = useApi();
  const detail = useAsync(() => api.getContent(id), [api, id]);

  return (
    <Modal open onClose={onClose} title="Content detail" size="large">
      <Modal.Section>
        {detail.loading && !detail.data ? (
          <LoadingCard label="Loading content detail" />
        ) : detail.error ? (
          <ErrorState message={detail.error} onRetry={detail.reload} />
        ) : detail.data ? (
          <BlockStack gap="400">
            <BlockStack gap="200">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingMd">
                  {detail.data.title || detail.data.slug || "(untitled)"}
                </Text>
                <StatusBadge status={detail.data.status} />
              </InlineStack>
              <InlineStack gap="400" wrap>
                <Field label="Type" value={titleCase(detail.data.content_type)} />
                <Field label="Language" value={detail.data.language} />
                <Field label="Slug" value={detail.data.slug} />
                <Field label="Created" value={formatDateTime(detail.data.created_at)} />
              </InlineStack>
            </BlockStack>

            <Divider />

            <BlockStack gap="200">
              <Text as="h4" variant="headingSm">
                Latest version
              </Text>
              {detail.data.latest_version ? (
                <BlockStack gap="300">
                  <InlineStack gap="200" blockAlign="center">
                    <Badge tone="new">{`v${detail.data.latest_version.version}`}</Badge>
                    {detail.data.latest_version.generated_by ? (
                      <Text as="span" tone="subdued" variant="bodySm">
                        by {detail.data.latest_version.generated_by}
                      </Text>
                    ) : null}
                  </InlineStack>
                  <Field label="SEO title" value={detail.data.latest_version.seo_title} />
                  <Field
                    label="SEO description"
                    value={detail.data.latest_version.seo_description}
                  />
                </BlockStack>
              ) : (
                <Text as="span" tone="subdued">
                  No version has been generated yet.
                </Text>
              )}
            </BlockStack>

            <Divider />

            <BlockStack gap="200">
              <Text as="h4" variant="headingSm">
                Article
              </Text>
              {/* body/excerpt live on the latest version (ContentVersionOut). */}
              {detail.data.latest_version?.excerpt ? (
                <Field label="Excerpt" value={detail.data.latest_version.excerpt} />
              ) : null}
              {detail.data.latest_version?.body ? (
                <Box
                  padding="300"
                  borderColor="border"
                  borderWidth="025"
                  borderRadius="200"
                  background="bg-surface-secondary"
                >
                  {/* Body is RankVine-generated HTML; sanitized as defence-in-depth. */}
                  <div
                    className="rankvine-article-body"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(detail.data.latest_version.body) }}
                  />
                </Box>
              ) : (
                <Text as="span" tone="subdued">
                  No article body yet.
                </Text>
              )}
            </BlockStack>

            <Divider />

            <BlockStack gap="200">
              <Text as="h4" variant="headingSm">
                Publish targets
              </Text>
              {detail.data.targets.length === 0 ? (
                <Text as="span" tone="subdued">
                  No publish targets yet.
                </Text>
              ) : (
                detail.data.targets.map((t) => (
                  <InlineStack key={t.id} align="space-between" blockAlign="center">
                    <Text as="span">
                      {titleCase(t.platform_resource_type)}
                      {t.published_at ? ` · published ${formatDateTime(t.published_at)}` : ""}
                    </Text>
                    <InlineStack gap="100" blockAlign="center">
                      {t.attempts > 0 ? (
                        <Text as="span" tone="subdued" variant="bodySm">
                          {t.attempts} attempt(s)
                        </Text>
                      ) : null}
                      <StatusBadge status={t.status} />
                    </InlineStack>
                  </InlineStack>
                ))
              )}
            </BlockStack>
          </BlockStack>
        ) : null}
      </Modal.Section>
    </Modal>
  );
}
